export type RowEntryId = number | string;

export type RowEntryMemo = { ids: ReadonlySet<RowEntryId>; signature: string };

export type RowEntry = { entered: ReadonlySet<RowEntryId>; memo: RowEntryMemo };

const NOTHING: ReadonlySet<RowEntryId> = new Set();

/**
 * Which rows just arrived, and what to remember for next time.
 *
 * Pure, and it owns the whole decision including the two cases that look like
 * arrivals and are not, because both are easy to argue about and cheap to pin:
 *
 * - The FIRST update seeds without entering anything. Every id is unseen then,
 *   so a plain diff would flash an entire first page on load.
 * - An update arriving on an EMPTY previous set seeds too. Almost every table
 *   here renders before its data does, so the real first paint is `[]` and the
 *   fetch that follows would otherwise read as the whole table arriving at once.
 *   The cost is that the very first row added to a genuinely empty table does
 *   not pulse; it is also the only row on screen, so there is nothing for the
 *   cue to distinguish it from.
 * - A moved `signature` seeds too. The grid reads one PAGE into `rows` under
 *   `paginationMode='server'` (likewise `filterMode` and `sortingMode`), so
 *   paging or refiltering hands over ids it has genuinely never seen. The
 *   signature is how the caller says a model moved in this same update.
 *
 * `memo.ids` holds only the current ids rather than every id ever seen, which is
 * what makes a removed row enter again when it comes back. That is deliberate:
 * the rule turns on a row ARRIVING, not on it being new to the session.
 */
export const resolveRowEntry = (ids: readonly RowEntryId[], signature: string, previous: RowEntryMemo | null): RowEntry => {
  const memo: RowEntryMemo = { ids: new Set(ids), signature };
  if (previous === null || previous.ids.size === 0 || previous.signature !== signature) return { entered: NOTHING, memo };

  const entered = new Set(ids.filter(id => !previous.ids.has(id)));
  return { entered: entered.size === 0 ? NOTHING : entered, memo };
};
